import React, { useEffect, useMemo, useRef, useCallback } from "react";
import CardTable from "../../../components/common/CardTable";
import EditableTable, { EditableTableColumn, EditableTableRow } from "../../../components/common/EditableTable";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchTeamRatingOptions, fetchServiceTypeOptions } from "../../../store/slices/costCenterSlice";
import { ValidationError } from "../../../components/common/ErrorBanner";

const radioOption = [
  { id: 2, label: "Yes", value: "yes" },
  { id: 3, label: "No", value: "no" }
];

interface TeamInfoFormProps {
  createTeam: boolean;
  editingTeam: boolean;
  editableRowMapped: EditableTableRow[];
  onEditTeam: (selectedRow: any) => void;
  onRowsChange: (updatedRows: EditableTableRow[]) => void;
  onCreateCostCenterForm: () => void;
  onUpdateTeamCancel: () => void;
  onUpdateTeam: () => void;
  validationErrors?: ValidationError[] | null;
  teamInfoLocked?: boolean;
}

const TeamInfoForm: React.FC<TeamInfoFormProps> = ({
  createTeam,
  editingTeam,
  editableRowMapped,
  onEditTeam,
  onRowsChange,
  onCreateCostCenterForm,
  onUpdateTeamCancel,
  onUpdateTeam,
  validationErrors,
  teamInfoLocked = false,
}) => {
  const latestRowsRef = useRef<EditableTableRow[]>(editableRowMapped);

  // Sync ref with prop changes to avoid stale data
  useEffect(() => {
    latestRowsRef.current = editableRowMapped;
  }, [editableRowMapped]);

  const handleInternalRowsChange = useCallback((rows: EditableTableRow[]) => {
    latestRowsRef.current = rows;
    onRowsChange(rows);
  }, [onRowsChange]);
  
  // Helper to get error message for a specific field
  const getFieldError = useCallback((fieldName: string): string | undefined => {
    const error = validationErrors?.find(err => err.field === fieldName);
    return error?.message;
  }, [validationErrors]);
  const dispatch = useAppDispatch();
  const { 
    teamRatingOptions: apiTeamRatingOptions, 
    serviceTypeOptions: apiServiceTypeOptions
  } = useAppSelector(
    (state) => state.costCenter
  );

  // Fetch team rating and service type options on component mount
  useEffect(() => {
    dispatch(fetchTeamRatingOptions());
    dispatch(fetchServiceTypeOptions());
  }, [dispatch]);

  // Transform API response to dropdown options format: "rating text"
  const teamRatingOptions = useMemo(() => {
    // Handle both direct array and nested data property
    const ratingData = Array.isArray(apiTeamRatingOptions) 
      ? apiTeamRatingOptions 
      : (apiTeamRatingOptions as { data?: unknown[] })?.data ?? [];
    
    if (ratingData.length === 0) {
      return [];
    }

    return ratingData.map((option: { rating: number; text: string }) => ({
      id: option.rating,
      label: `${option.rating} ${option.text}`,
      value: `${option.rating} ${option.text}`,
    }));
  }, [apiTeamRatingOptions]);

  // Transform service type API response to dropdown options
  const serviceTypeOptions = useMemo(() => {
    // Handle both direct array and nested data property
    const serviceData = Array.isArray(apiServiceTypeOptions)
      ? apiServiceTypeOptions
      : (apiServiceTypeOptions as { data?: unknown[] })?.data ?? [];
    
    if (serviceData.length === 0) {
      return [];
    }

    return serviceData.map((option: string | { id?: number; code?: number; name?: string; label?: string; text?: string; value?: string }, index: number) => {
      // If option is a string, use it directly
      if (typeof option === 'string') {
        return {
          id: index + 1,
          label: option,
          value: option,
        };
      }
      // Otherwise handle as object - use fallback empty string for safety
      return {
        id: option.id ?? option.code ?? index + 1,
        label: option.name ?? option.label ?? option.text ?? '',
        value: String(option.code ?? option.value ?? option.name ?? ''),
      };
    });
  }, [apiServiceTypeOptions]);

  // Update columns with fetched options and error messages
  const createTeamColumns: EditableTableColumn[] = useMemo(
    () => [
      { key: "id", label: "ID", isEdit: false },
      { 
        key: 'nameOfUBT', 
        label: 'Name of UBT', 
        isEdit: true,
        disabled: teamInfoLocked,
        error: getFieldError('nameOfUBT')
      },
      {
        key: 'dateTeamEstablished',
        label: 'Date Team Established',
        isEdit: true,
        editType: "date",
        disabled: teamInfoLocked,
        error: getFieldError('dateTeamEstablished')
      },
      {
        key: 'teamRating',
        label: 'Team Rating',
        isEdit: true,
        editType: "dropdown",
        options: teamRatingOptions
      },
      {
        key: 'serviceType',
        label: 'Service Type',
        isEdit: true,
        editType: "dropdown",
        options: serviceTypeOptions
      },
      {
        key: 'usingHuddles',
        label: 'Using Huddles',
        isEdit: true,
        editType: "dropdown",
        options: radioOption
      },
      {
        key: 'defaultMembership',
        label: 'Default Membership',
        isEdit: true,
        editType: "dropdown",
        options: radioOption
      },
    ],
    [teamRatingOptions, serviceTypeOptions, getFieldError, teamInfoLocked]
  );

  // Update edit team columns with fetched options and error messages
  const editTeamColumn: EditableTableColumn[] = useMemo(
    () => {
      const columns: EditableTableColumn[] = [
        { key: "id", label: "ID", isEdit: false },
        { 
          key: 'nameOfUBT', 
          label: 'Name of UBT', 
          isEdit: true,
          error: getFieldError('nameOfUBT')
        },
        {
          key: 'dateTeamEstablished',
          label: 'Date Team Established',
          isEdit: true,
          editType: "date",
          error: getFieldError('dateTeamEstablished')
        },
        {
          key: 'teamRating',
          label: 'Team Rating',
          isEdit: false
        },
        {
          key: 'serviceType',
          label: 'Service Type',
          isEdit: true,
          editType: "dropdown",
          options: serviceTypeOptions
        },
        {
          key: 'usingHuddles',
          label: 'Using Huddles',
          isEdit: true,
          editType: "dropdown",
          options: radioOption
        },
        {
          key: 'defaultMembership',
          label: 'Default Membership',
          isEdit: true,
          editType: "dropdown",
          options: radioOption
        },
        { key: 'selectAction', label: 'Edit' },
      ];
      return columns;
    },
    [serviceTypeOptions, getFieldError]
  );

  return (
    <>
      <CardTable>
        <EditableTable 
          columns={createTeam ? createTeamColumns : editTeamColumn} 
          rows={editableRowMapped} 
          actionKey="view" 
          onActionClick={onEditTeam} 
          isEditMode={editingTeam || createTeam} 
          className="no-zebra-stripes" 
          onRowsChange={handleInternalRowsChange}
        />
      </CardTable>
      {createTeam && (
        <div className="flex gap-x-4 mt-8!">
          <button
            type="button"
            className="button py-0! -inverted hover:text-white!"
            data-analytics-type="button"
            data-analytics-click="Default"
            onClick={() => {
              // flush latest edits to parent before creating cost center
              // Make sure we're using the absolute latest state from EditableTable
              onRowsChange(latestRowsRef.current);
              // Small delay to ensure state is fully updated before validation
              setTimeout(() => onCreateCostCenterForm(), 0);
            }}
          >
            Create Team
          </button>
        </div>
      )}
      {editingTeam && (
        <div className="flex gap-x-4 mt-8!">
          <button
            className="button py-0! -inverted hover:text-white!"
            data-analytics-type="button"
            data-analytics-click="Default"
            onClick={onUpdateTeamCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button -primary -light block w-full! py-0! -inverted hover:text-white!"
            data-analytics-type="button"
            data-analytics-click="Default"
            onClick={() => {
              // flush latest edits to parent before saving
              onRowsChange(latestRowsRef.current);
              onUpdateTeam();
            }}
          > 
            Save
          </button>
        </div>
      )}
    </>
  );
};

export default TeamInfoForm;
